import React, { useState, useEffect, useMemo } from 'react';
import config from '../../config';

const Package = () => {
  // State initialization
  const [entityNames, setEntityNames] = useState([]);
  const [stateNames, setStateNames] = useState([]);
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [selectedStateId, setSelectedStateId] = useState("");
  const [selectedPackage, setSelectedPackage] = useState("Standard");
  const [planData, setPlanData] = useState([]);
  const [additionalData, setAdditionalData] = useState([]);
  const [toggledButtons, setToggledButtons] = useState({});
  const [apiData, setApiData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Memoized values
  const urlParams = useMemo(() => {
    const url = new URL(window.location.href);
    return {
      entity: url.searchParams.get("entity"),
      state: url.searchParams.get("state")
    };
  }, []);

  const resultData = useMemo(() => {
    const data = localStorage.getItem('formOrderResult');
    return data ? JSON.parse(data) : null;
  }, []);

  const extractedId = useMemo(() => resultData?.data?.id || null, [resultData]);

  // Combined data fetching with error boundaries
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        // Fetch all data in parallel
        const [entities, states, plans, packages, formData] = await Promise.all([
          fetch(`${config.baseUrl}/entity/list`).then(res => res.json()),
          fetch(`${config.baseUrl}/state/list`).then(res => res.json()),
          fetch(`${config.baseUrl}/business/plan`).then(res => res.json()),
          fetch(`${config.baseUrl}/business/packages`).then(res => res.json()),
          extractedId ? fetch(`${config.baseUrl}/registration/get-form-1/${extractedId}`).then(res => res.json()) : Promise.resolve(null)
        ]);

        // Set entity and state data
        if (entities.status === "success") setEntityNames(entities.data);
        if (states.status === "success") setStateNames(states.data);

        // Set default selections from URL params
        if (urlParams.entity && entities.data) {
          const defaultEntity = entities.data.find(e => e.id === parseInt(urlParams.entity));
          if (defaultEntity) {
            setSelectedEntityId(defaultEntity.id);
          }
        }

        if (urlParams.state && states.data) {
          const defaultState = states.data.find(s => s.id === parseInt(urlParams.state));
          if (defaultState) {
            setSelectedStateId(defaultState.id);
          }
        }

        // Set plan and package data
        if (plans.status === "success") setPlanData(plans.data);
        if (packages.status === "success") setAdditionalData(packages.data);

        // Set form data if available
        if (formData?.status === "success") {
          setApiData(formData.data);
          // Apply form data to state
          if (formData.data) {
            setSelectedPackage(formData.data.package || "Standard");
            // Additional initialization logic...
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [extractedId, urlParams.entity, urlParams.state]);

  // Optimized render functions
  const renderPackageHeaders = useMemo(() => {
    if (isLoading) return Array(3).fill(<th><div className="skeleton-loader" style={{ height: '40px', width: '100px' }} /></th>);

    return planData.map((filing) => (
      <th key={filing.plan_name}>
        <div className="table-btn" style={{ textAlign: "center" }}>
          <button
            type="button"
            className={`basic-btn2 ${selectedPackage === filing.plan_name ? "btn-red-600" : ""}`}
            onClick={() => setSelectedPackage(filing.plan_name)}
          >
            {filing.plan_name} Filing Time
          </button>
        </div>
      </th>
    ));
  }, [planData, selectedPackage, isLoading]);

  const renderPackagePrices = useMemo(() => {
    if (isLoading) return Array(3).fill(<th style={{ padding: "18px 20px", textAlign: "center" }}><div className="skeleton-loader" style={{ height: '60px', width: '120px' }} /></th>);

    return planData.map((filing) => (
      <th
        style={{ padding: "18px 20px", textAlign: "center" }}
        key={filing.plan_name}
      >
        <p><strong>Select Package</strong></p>
        <h5><span>${filing.price}</span> + State Fee</h5>
      </th>
    ));
  }, [planData, isLoading]);

  const renderAdditionalDataRows = useMemo(() => {
    if (isLoading) return Array(5).fill(
      <tr style={{ backgroundColor: "var(--white)" }}>
        <td style={{ padding: 15, borderRadius: 6 }}>
          <div className="skeleton-loader" style={{ height: '20px', width: '150px' }} />
        </td>
        {Array(3).fill(<td style={{ padding: "18px 20px", textAlign: "center" }}>
          <div className="skeleton-loader" style={{ height: '30px', width: '80px' }} />
        </td>)}
      </tr>
    );

    return additionalData.map((item) => (
      <tr style={{ backgroundColor: "var(--white)" }} key={item.id}>
        <td style={{ padding: 15, borderRadius: 6 }}>
          <h6 style={{ color: "var(--black)", marginBottom: 0, fontSize: 14 }}>
            {item.name}
          </h6>
          <div className="select-box">{item.description}</div>
        </td>

        {planData.map((filing) => (
          <td
            style={{ padding: "18px 20px", textAlign: "center" }}
            key={`${item.id}-${filing.plan_name}`}
          >
            <div>
              <span>
                {item.items
                  .filter((subItem) => subItem.plan_name === filing.plan_name)
                  .map((subItem) => (
                    subItem.price === 0 ? (
                      <img 
                        src="images/Vector (2).png" 
                        alt="Included" 
                        key={subItem.id}
                        width="20"
                        height="20"
                        loading="lazy"
                      />
                    ) : (
                      <button key={subItem.id}>
                        {toggledButtons[subItem.id]?.active
                          ? ` $${subItem.price}`
                          : ` $${subItem.price}`}
                      </button>
                    )
                  ))}
              </span>
            </div>
          </td>
        ))}
      </tr>
    ));
  }, [additionalData, planData, toggledButtons, isLoading]);

  return (
    <div className="comparion-part">
      <div className="container">
        <div className="table-responsive">
          <table style={{ width: "100%" }}>
            <tbody>
              <tr>
                <th />
                {renderPackageHeaders}
              </tr>
              <tr style={{
                width: "100%",
                backgroundColor: "var(--white)",
                borderRadius: 4,
              }}>
                <th style={{ padding: "18px 20px", borderRadius: 6 }}>
                  <h2>Compare Packages</h2>
                  <h6><strong>One-time fee</strong></h6>
                  <p>
                    Unlike other companies that charge annual fees, our
                    business formation fee is one-time.
                  </p>
                </th>
                {renderPackagePrices}
              </tr>
              {renderAdditionalDataRows}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Package;